
import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

interface Settings {
  restaurantName: {
    ru: string;
    en: string;
    ky: string;
    zh: string;
  };
  currency: string;
  tableCount: number;
  workingHours?: {
    open: string;  // "09:00"
    close: string; // "22:00"
  };
  minOrderAmount?: number;
  /**
   * Бета: гость видит предупреждение, что сервис ещё обкатывается, и
   * извинение за возможные сбои. На сами заказы не влияет — они обычные.
   */
  betaMode?: boolean;
  /**
   * Ссылка, где гость сам выбирает стол (/select-table). Нужна на обкатке,
   * пока QR-коды не напечатаны и не расставлены. В обычной работе выключена:
   * иначе с улицы можно заказать на любой занятый стол.
   */
  tableSelectEnabled?: boolean;
}

interface SettingsContextType {
  settings: Settings;
  loading: boolean;
  updateSettings: (newSettings: Partial<Settings>) => Promise<void>;
}

const defaultSettings: Settings = {
  restaurantName: {
    ru: 'Долина Вкуса',
    en: 'Valley of Taste',
    ky: 'Даам өрөөнү',
    zh: '味之谷'
  },
  currency: 'KGS',
  tableCount: 20,
  workingHours: { open: '09:00', close: '22:00' },
  minOrderAmount: 0,
  betaMode: false,
  tableSelectEnabled: false
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'settings', 'general'), (doc) => {
      if (doc.exists()) {
        const data = doc.data() as Settings;
        // Миграция легаси значения: было "с" (непонятное сокращение) → "KGS".
        // Это нормализация на чтении, админ при желании может переопределить.
        if (data.currency === 'с') data.currency = 'KGS';
        setSettings(data);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error fetching settings:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const updateSettings = async (newSettings: Partial<Settings>) => {
    try {
      await setDoc(doc(db, 'settings', 'general'), {
        ...settings,
        ...newSettings
      }, { merge: true });
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  };

  return (
    <SettingsContext.Provider value={{ settings, loading, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
